import React from 'react';
import { Trans } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Collapse, Badge } from 'reactstrap';
import SidebarUserBlock from './SidebarUserBlock';
import Menu from '../../Menu.js';
import useStatisticStore from '../../stores/statisticsStore';
import useAuthStore from '../../stores/authStore.js';

const SidebarItemHeader = ({ item }) => (
  <li className="nav-heading">
    <span>
      <Trans i18nKey={item.translate}>{item.heading}</Trans>
    </span>
  </li>
);

const SidebarItem = ({ item, isActive }) => (
  <li
    className={isActive ? 'active' : ''}
    data-tut={item.name === 'Insert Applicant' ? 'reactour__create_applicant' : null}
  >
    <Link to={item.path} title={item.name}>
      {item.icon && <em className={item.icon}></em>}
      <span>
        <Trans i18nKey={item.translate}>{item.name}</Trans>
      </span>
    </Link>
  </li>
);

const SidebarItemWithBadge = ({ item, isActive, value }) => (
  <li className={isActive ? 'active' : ''}>
    <Link to={item.path} title={item.name}>
      {item.label && (
        <Badge tag="div" className="float-right" color={item.label.color}>
          {value}
        </Badge>
      )}
      {item.icon && <em className={item.icon}></em>}
      <span>
        <Trans i18nKey={item.translate}>{item.name}</Trans>
      </span>
    </Link>
  </li>
);

const SidebarSubItem = ({ item, isActive, handler, children, isOpen }) => (
  <li className={isActive ? 'active' : ''}>
    <div className="nav-item" onClick={handler}>
      {item.label && (
        <Badge tag="div" className="float-right" color={item.label.color}>
          {item.label.value}
        </Badge>
      )}
      {item.icon && <em className={item.icon}></em>}
      <span>
        <Trans i18nKey={item.translate}>{item.name}</Trans>
      </span>
    </div>
    <Collapse isOpen={isOpen}>
      <ul id={item.path} className="sidebar-nav sidebar-subnav">
        {children}
      </ul>
    </Collapse>
  </li>
);

const SidebarSubHeader = ({ item }) => <li className="sidebar-subnav-header">{item.name}</li>;

const Sidebar = () => {
  const location = useLocation();
  const [collapse, setCollapse] = React.useState({});
  const statistic = useStatisticStore((state) => state.statistic);
  const user = useAuthStore((state) => state.user);

  React.useEffect(() => {
    buildCollapseList();
    // eslint-disable-next-line
  }, []);

  const buildCollapseList = () => {
    let collapseState = {};
    Menu.filter(({ heading }) => !heading).forEach(({ name, path, submenu }) => {
      collapseState[name] = routeActive(submenu ? submenu.map(({ path }) => path) : path);
    });
    setCollapse(collapseState);
  };

  const routeActive = (paths) => {
    paths = Array.isArray(paths) ? paths : [paths];
    return paths.some((p) => location.pathname.indexOf(p) > -1);
  };

  const toggleItemCollapse = (stateName) => {
    setCollapse((prev) => {
      const newCollapse = {};
      Object.keys(prev).forEach((c) => {
        newCollapse[c] = c === stateName ? !prev[c] : false;
      });
      return newCollapse;
    });
  };

  const getSubRoutes = (item) => item.submenu.map(({ path }) => path);

  const itemType = (item) => {
    if (item.heading) return 'heading';
    if (!item.submenu) return 'menu';
    if (item.submenu) return 'submenu';
  };

  return (
    <aside className="aside-container">
      <div className="aside-inner">
        <nav data-sidebar-anyclick-close="" className="sidebar">
          <ul className="sidebar-nav">
            <li className="has-user-block">
              <SidebarUserBlock />
            </li>
            {Menu.map((item, i) => {
              // heading
              if (itemType(item) === 'heading') {
                if (item.roles.includes(user?.role)) {
                  return <SidebarItemHeader item={item} key={i} />;
                }
              } else {
                if (itemType(item) === 'menu') {
                  if (item.roles.includes(user?.role)) {
                    if (item.name === 'Applicants') {
                      if (statistic?.newApplicants > 0) {
                        return (
                          <SidebarItemWithBadge
                            isActive={routeActive(item.path)}
                            item={item}
                            value={statistic?.newApplicants}
                            key={i}
                          />
                        );
                      } else {
                        return <SidebarItem isActive={routeActive(item.path)} item={item} key={i} />;
                      }
                    }
                    if (item.name === 'Applications') {
                      if (statistic?.newApplications > 0) {
                        return (
                          <SidebarItemWithBadge
                            isActive={routeActive(item.path)}
                            item={item}
                            value={statistic?.newApplications}
                            key={i}
                          />
                        );
                      } else {
                        return <SidebarItem isActive={routeActive(item.path)} item={item} key={i} />;
                      }
                    }
                    return (
                      <SidebarItem
                        data-tut="reactour__create_applicant"
                        isActive={routeActive(item.path)}
                        item={item}
                        key={i}
                      />
                    );
                  }
                }

                if (itemType(item) === 'submenu') {
                  if (item.roles.includes(user?.role)) {
                    return (
                      <SidebarSubItem
                        item={item}
                        isOpen={collapse[item.name]}
                        handler={() => toggleItemCollapse(item.name)}
                        isActive={routeActive(getSubRoutes(item))}
                        key={i}
                      >
                        <SidebarSubHeader item={item} key={i} />
                        {item.submenu.map((subitem, j) => (
                          <SidebarItem key={j} item={subitem} isActive={routeActive(subitem.path)} />
                        ))}
                      </SidebarSubItem>
                    );
                  }
                }
              }
              return null;
            })}
          </ul>
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar;
